import { z } from "zod";

/**
 * Spec 047 — shared claim-protocol contract (single source of truth).
 *
 * Both the Nest server and the Chrome extension import these pure Zod schemas
 * and validate at their own boundary, so the wire contract cannot drift.
 *
 * Protocol v2 replaces the ambiguous claim-or-204 contract with exactly one
 * of three decisions:
 *   - `claimed`  — one exclusive application handoff (payload identical to v1)
 *   - `complete` — the queue is genuinely empty (reason is always queue_empty)
 *   - `blocked`  — no work can be assigned now; controlled reason, optional
 *                  `retryAt` (safe to auto-resume) and optional `limit`
 *
 * A `blocked` decision WITH `retryAt` keeps the run alive (temporary pause);
 * WITHOUT `retryAt` it halts the run and asks the user to act. Unknown or
 * invalid decisions fail closed as update_required — they never trigger a
 * submission.
 *
 * Protocol v1 remains: 200 claim payload or 204. v2 requests are v1 requests
 * plus optional metadata; the metadata is optional so v1 requests still parse.
 */

// ─── Request ────────────────────────────────────────────────────────────────

export const extensionInstallIdSchema = z.string().min(1).max(128);

/** Semver-ish release string, e.g. "1.2.3" or "1.2.3-beta.1" (≤ 32 chars). */
export const extensionVersionSchema = z
  .string()
  .max(32)
  .regex(/^\d+\.\d+\.\d+(?:[+-][\d.A-Za-z-]+)?$/);

export const extensionRunIdSchema = z.string().min(1).max(128);

/** Board-identity values (e.g. the LinkedIn member id), per supported board. */
export const boardIdentityValueSchema = z.string().min(1).max(64);

/**
 * Fields every claim request carries, independent of protocol version. Board
 * strictness (which boards are valid) is layered on by each consumer — the
 * Nest DTO narrows to the JobBoard enum, the extension to its handled boards.
 */
export const claimRequestBaseSchema = z.object({
  installId: extensionInstallIdSchema,
  supportedBoards: z.array(z.string().min(1)).min(1),
  boardIdentities: z.record(z.string().min(1), boardIdentityValueSchema).optional().default({}),
});

/**
 * v2 request metadata. Deliberately `.optional()` WITHOUT a zod default so a
 * v1 request parses to exactly its v1 shape (legacy contract tests pin this);
 * use `effectiveProtocolVersion` to resolve the version at the boundary.
 */
export const claimProtocolMetadataSchema = z.object({
  protocolVersion: z.number().int().min(1).max(2).optional(),
  extensionVersion: extensionVersionSchema.optional(),
  runId: extensionRunIdSchema.optional(),
});

export const claimRequestV2Schema = claimRequestBaseSchema.merge(claimProtocolMetadataSchema);

export type ClaimRequestV2 = z.infer<typeof claimRequestV2Schema>;

/** Protocol v3 is exclusively for a server-authorized bounded batch run. */
export const claimRequestV3Schema = claimRequestBaseSchema
  .extend({
    protocolVersion: z.literal(3),
    extensionVersion: extensionVersionSchema,
    runId: extensionRunIdSchema,
    batchRunId: z.string().min(1),
  })
  .strict();

export type ClaimRequestV3 = z.infer<typeof claimRequestV3Schema>;

/** General v1/v2 and bounded v3 requests are intentionally disjoint. */
export const anyClaimRequestSchema = z.union([claimRequestV2Schema, claimRequestV3Schema]);

/** Absent protocolVersion means v1 — old clients must keep working. */
export function effectiveProtocolVersion(req: { protocolVersion?: number }): 1 | 2 | 3 {
  if (req.protocolVersion === 3) return 3;
  return req.protocolVersion === 2 ? 2 : 1;
}

// ─── Claim payload (unchanged from v1 — the handoff itself) ─────────────────

export const claimPayloadSchema = z.object({
  applicationId: z.string(),
  board: z.string().min(1),
  jobUrl: z.string().url(),
  coverLetter: z.string(),
  deterministicFillData: z.record(z.string(), z.unknown()),
  expectedProfileId: z.string().nullable(),
  expectedSubmissionMethod: z.literal("LINKEDIN_EASY_APPLY_EXTENSION"),
  claimExpiresAt: z.string().datetime(),
});

export type ClaimPayload = z.infer<typeof claimPayloadSchema>;

// ─── Claim decision (v2 response) ───────────────────────────────────────────

/**
 * Closed blocker taxonomy. Claim blockers describe whether more work may be
 * assigned AT ALL (account/allowance/configuration state); per-job result
 * reasons (closed_job, missing_answer, …) are a separate vocabulary carried
 * by the result-report endpoint — never mixed into this enum.
 */
export const CLAIM_BLOCK_REASONS = [
  "account_mismatch",
  /**
   * The server could not READ the account identity at all (no stored
   * platformAccountId AND the extension reported none — e.g. no LinkedIn tab
   * was open to scrape the member-id). Distinct from account_mismatch:
   * nothing is "wrong" with the account, so the user copy must tell them to
   * open LinkedIn, not to switch accounts (spec 047 review).
   */
  "identity_unverifiable",
  "daily_limit",
  "monthly_limit",
  "burst_limit",
  "automation_paused",
  "cohort_disabled",
  "update_required",
] as const;

export const claimBlockReasonSchema = z.enum(CLAIM_BLOCK_REASONS);

export type ClaimBlockReason = (typeof CLAIM_BLOCK_REASONS)[number];

/** Assignment decision — strict so unknown fields from newer servers reject. */
export const claimedDecisionSchema = z
  .object({
    kind: z.literal("claimed"),
    claim: claimPayloadSchema,
  })
  .strict();

/** The only valid completion reason: there is genuinely no eligible work. */
export const completeDecisionSchema = z
  .object({
    kind: z.literal("complete"),
    reason: z.literal("queue_empty"),
  })
  .strict();

/**
 * No work can be assigned now. `retryAt` present → temporary pause, the run
 * stays active and resumes automatically; absent → halt, user action needed.
 * `limit` carries the daily/monthly/burst ceiling when relevant.
 */
export const blockedDecisionSchema = z
  .object({
    kind: z.literal("blocked"),
    reason: claimBlockReasonSchema,
    retryAt: z.string().datetime().optional(),
    limit: z.number().int().min(0).optional(),
  })
  .strict();

export const claimDecisionSchema = z.discriminatedUnion("kind", [
  claimedDecisionSchema,
  completeDecisionSchema,
  blockedDecisionSchema,
]);

export type ClaimDecision = z.infer<typeof claimDecisionSchema>;
export type ClaimedDecision = z.infer<typeof claimedDecisionSchema>;
export type CompleteDecision = z.infer<typeof completeDecisionSchema>;
export type BlockedDecision = z.infer<typeof blockedDecisionSchema>;

// ─── Bounded-batch claim decision (v3 response) ───────────────────────────

export const BATCH_CLAIM_BLOCK_REASONS = [
  ...CLAIM_BLOCK_REASONS,
  "batch_preparing",
  "batch_stopped",
  "batch_run_expired",
  "batch_run_conflict",
  "batch_not_ready",
  "application_in_progress",
] as const;

export const batchClaimBlockReasonSchema = z.enum(BATCH_CLAIM_BLOCK_REASONS);

export const batchCompleteDecisionSchema = z
  .object({ kind: z.literal("complete"), reason: z.literal("batch_complete") })
  .strict();

export const batchBlockedDecisionSchema = z
  .object({
    kind: z.literal("blocked"),
    reason: batchClaimBlockReasonSchema,
    retryAt: z.string().datetime().optional(),
    limit: z.number().int().min(0).optional(),
  })
  .strict();

export const batchClaimDecisionSchema = z.discriminatedUnion("kind", [
  claimedDecisionSchema,
  batchCompleteDecisionSchema,
  batchBlockedDecisionSchema,
]);

export type BatchClaimDecision = z.infer<typeof batchClaimDecisionSchema>;

// ─── Dual-protocol response parsing (extension side) ────────────────────────

export type ClaimServerResponse =
  | { protocol: "v1"; claim: ClaimPayload }
  | { protocol: "v2"; decision: ClaimDecision };

/**
 * Parse a /claim HTTP 200 body under either protocol. A body carrying a
 * `kind` discriminator is a v2 decision; anything else is a legacy v1 claim
 * payload (a 204 arrives as null and never reaches this function).
 *
 * Throws on any invalid shape — the caller must fail closed (update_required)
 * and never open a job tab on an unparseable response.
 */
export function parseClaimServerResponse(raw: unknown): ClaimServerResponse {
  if (
    raw !== null &&
    typeof raw === "object" &&
    "kind" in raw &&
    typeof (raw as { kind: unknown }).kind === "string"
  ) {
    return { protocol: "v2", decision: claimDecisionSchema.parse(raw) };
  }
  return { protocol: "v1", claim: claimPayloadSchema.parse(raw) };
}

/** Parse a response only after the caller has sent a protocol-v3 batch claim. */
export function parseBatchClaimServerResponse(raw: unknown): BatchClaimDecision {
  return batchClaimDecisionSchema.parse(raw);
}
